import { useMemo, useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChevronRight,
  Link2,
  Link2Off,
  MessageSquare,
  QrCode,
  ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { ErrorState, Page, PageBody, PageHeader, Panel, Skeleton } from '@/ui/Panel';
import { Button } from '@/ui/Button';
import { Field, Input, Select, Switch, Textarea } from '@/ui/Field';
import { Badge, Code } from '@/ui/Badge';
import { Callout } from '@/ui/Callout';
import { api, errorMessage } from '@/lib/api/client';
import { useShortcutHint } from '@/app/StatusLine';
import { dateTime } from '@/lib/format';
import {
  changedKeys,
  fieldValue,
  isBooleanOn,
  secretPreview,
  serverValue,
  SETTING_SECTIONS,
  type SettingField,
  type SettingSection,
} from '@/features/system/settings-schema';
import { useWhatsAppLink, type WhatsAppStatus } from '@/features/system/useWhatsAppLink';
import type { SettingsResponse } from '@/lib/api/types';

/**
 * Store settings.
 *
 * `GET /settings` is one flat key-value bag; the sections here come from
 * `features/system/settings-schema.ts`. Each section tracks its own dirty state
 * and its Save sends **only the keys that changed** — a whole-bag PUT would
 * rewrite rows nobody touched and stamp `updated_at` across the table.
 */
export const Route = createFileRoute('/_app/settings')({
  staticData: { crumb: 'Settings' },
  component: SettingsScreen,
});

function SettingsScreen() {
  const queryClient = useQueryClient();

  useShortcutHint([{ keys: 'Esc', label: 'Discard section' }]);

  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get<SettingsResponse>('/settings'),
  });

  // The draft holds ONLY the keys the operator has touched. Everything else
  // renders straight from the query, so a background refetch updates the page
  // without ever clobbering — or falsely dirtying — an in-progress edit.
  const [draft, setDraft] = useState<Record<string, string>>({});

  const save = useMutation({
    mutationFn: (payload: Record<string, string>) =>
      api.put<SettingsResponse>('/settings', payload),
    onSuccess: async (_result, payload) => {
      await queryClient.invalidateQueries({ queryKey: ['settings'] });
      // Drop the saved keys from the draft: they now come from the server
      // again, and a secret's box empties back to "keep the current key".
      setDraft((prev) => {
        const next = { ...prev };
        for (const key of Object.keys(payload)) delete next[key];
        return next;
      });
      toast.success(`Saved ${Object.keys(payload).length} setting(s)`);
    },
    onError: (error) => toast.error('Save failed', { description: errorMessage(error) }),
  });

  if (settings.isLoading) {
    return (
      <Page>
        <PageHeader eyebrow="System" title="Settings" />
        <PageBody className="space-y-3">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-40 w-full" />
        </PageBody>
      </Page>
    );
  }

  if (settings.isError) {
    return (
      <Page>
        <PageHeader eyebrow="System" title="Settings" />
        <PageBody>
          <ErrorState
            message={errorMessage(settings.error)}
            onRetry={() => void settings.refetch()}
          />
        </PageBody>
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader
        eyebrow="System"
        title="Settings"
        subtitle="Store-wide configuration. Per-register hardware settings live on each register."
      />

      <PageBody className="space-y-4">
        <div className="grid gap-3 xl:grid-cols-2">
          {SETTING_SECTIONS.map((section) => (
            <SettingsSection
              key={section.id}
              section={section}
              settings={settings.data}
              draft={draft}
              onChange={(key, value) => setDraft((prev) => ({ ...prev, [key]: value }))}
              onSave={(payload) => save.mutate(payload)}
              saving={save.isPending}
            />
          ))}

          <WhatsAppPanel />
          <Panel title="Labels" description="Design templates for your label stations.">
            <Link
              to="/settings/label-templates"
              search={{ kind: 'shelf' }}
              className="text-signal-ink hover:underline"
            >
              Label templates
            </Link>
          </Panel>

          <Panel title="Register" description="Configured here, consumed at the lane.">
            <Link
              to="/settings/quick-panel"
              className="hover:bg-surface-hover -m-3 flex items-center gap-3 p-3 transition-colors"
            >
              <span className="min-w-0 flex-1">
                <span className="text-ink block text-sm">Quick panel items</span>
                <span className="text-2xs text-ink-faint block">
                  The shortcut products on the register's quick-access board.
                </span>
              </span>
              <ChevronRight className="text-ink-faint size-3.5 shrink-0" />
            </Link>
          </Panel>
        </div>
      </PageBody>
    </Page>
  );
}

/* ── One section ──────────────────────────────────────────────────────────── */

function SettingsSection({
  section,
  settings,
  draft,
  onChange,
  onSave,
  saving,
}: {
  section: SettingSection;
  settings: SettingsResponse | undefined;
  draft: Record<string, string>;
  onChange: (key: string, value: string) => void;
  onSave: (payload: Record<string, string>) => void;
  saving: boolean;
}) {
  const dirty = useMemo(
    () => changedKeys(section, draft, settings),
    [section, draft, settings]
  );

  function submit() {
    const payload: Record<string, string> = {};
    for (const key of dirty) payload[key] = draft[key] ?? '';
    onSave(payload);
  }

  function discard() {
    // Writing the server's value back marks the field untouched again.
    for (const field of section.fields) onChange(field.key, serverValue(field, settings));
  }

  return (
    <Panel
      title={section.title}
      description={section.description}
      actions={
        dirty.length > 0 ? (
          <>
            <Button size="xs" variant="ghost" onClick={discard}>
              Discard
            </Button>
            <Button size="xs" variant="primary" loading={saving} onClick={submit}>
              Save {dirty.length}
            </Button>
          </>
        ) : null
      }
      footer={
        dirty.length > 0 ? (
          <span className="text-signal-ink">
            Unsaved: {dirty.map((k) => k.replace(/_/g, ' ')).join(', ')}
          </span>
        ) : undefined
      }
    >
      <div className="space-y-3">
        {section.fields.map((field) => (
          <SettingControl
            key={field.key}
            field={field}
            settings={settings}
            value={fieldValue(field, draft, settings)}
            dirty={dirty.includes(field.key)}
            onChange={(value) => onChange(field.key, value)}
          />
        ))}
      </div>
    </Panel>
  );
}

function SettingControl({
  field,
  settings,
  value,
  dirty,
  onChange,
}: {
  field: SettingField;
  settings: SettingsResponse | undefined;
  value: string;
  dirty: boolean;
  onChange: (value: string) => void;
}) {
  const control = field.control;

  if (control.kind === 'boolean') {
    return (
      <div className="border-line-soft flex items-center justify-between gap-4 border-b pb-3 last:border-0 last:pb-0">
        <div className="min-w-0">
          <div className="text-ink text-sm">{field.label}</div>
          {field.hint ? (
            <div className="text-2xs text-ink-faint mt-0.5">{field.hint}</div>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {dirty ? <Badge tone="signal">changed</Badge> : null}
          <Switch
            checked={isBooleanOn(value, field.fallback)}
            onCheckedChange={(next) => onChange(String(next))}
          />
        </div>
      </div>
    );
  }

  if (control.kind === 'secret') {
    const preview = secretPreview(settings, field.key);
    return (
      <Field
        label={field.label}
        hint={
          value
            ? 'This replaces the stored key when you save.'
            : 'Leave empty to keep the existing key — an empty value never wipes it.'
        }
        action={
          preview ? (
            <span className="text-2xs text-positive flex items-center gap-1">
              <ShieldCheck className="size-3" /> installed · …{preview}
            </span>
          ) : (
            <span className="text-2xs text-ink-faint">not set</span>
          )
        }
      >
        <Input
          type="password"
          mono
          autoComplete="off"
          value={value}
          placeholder={preview ? `•••••••••${preview}` : 'Paste a key'}
          onChange={(e) => onChange(e.currentTarget.value)}
        />
      </Field>
    );
  }

  if (control.kind === 'select') {
    return (
      <Field label={field.label} hint={field.hint}>
        <Select value={value} onChange={(e) => onChange(e.currentTarget.value)}>
          {control.options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      </Field>
    );
  }

  if (control.kind === 'textarea') {
    return (
      <Field label={field.label} hint={field.hint}>
        <Textarea
          rows={control.rows ?? 3}
          value={value}
          onChange={(e) => onChange(e.currentTarget.value)}
        />
      </Field>
    );
  }

  if (control.kind === 'number') {
    return (
      <Field label={field.label} hint={field.hint}>
        <Input
          mono
          inputMode="decimal"
          value={value}
          suffix={
            control.unit ? <span className="text-2xs">{control.unit}</span> : undefined
          }
          onChange={(e) => onChange(e.currentTarget.value)}
        />
      </Field>
    );
  }

  return (
    <Field label={field.label} hint={field.hint}>
      <Input
        value={value}
        placeholder={control.placeholder}
        onChange={(e) => onChange(e.currentTarget.value)}
      />
    </Field>
  );
}

/* ── WhatsApp link ────────────────────────────────────────────────────────── */

const WA_TONE: Record<
  WhatsAppStatus,
  'positive' | 'warn' | 'negative' | 'neutral' | 'signal'
> = {
  connected: 'positive',
  connecting: 'warn',
  qr: 'signal',
  disconnected: 'neutral',
  error: 'negative',
};

function WhatsAppPanel() {
  const link = useWhatsAppLink();
  const { snapshot } = link;

  return (
    <Panel
      title="WhatsApp"
      description="Persistent link used to deliver receipts and gift cards."
      actions={
        link.canControl ? (
          <>
            <Button
              size="xs"
              variant={snapshot.status === 'connected' ? 'ghost' : 'primary'}
              icon={
                snapshot.status === 'qr' ? (
                  <QrCode className="size-3" />
                ) : (
                  <Link2 className="size-3" />
                )
              }
              disabled={
                snapshot.status === 'connected' || snapshot.status === 'connecting'
              }
              onClick={() => link.send({ type: 'connect' })}
            >
              {snapshot.status === 'qr' ? 'Refresh QR' : 'Link device'}
            </Button>
            {snapshot.status !== 'disconnected' ? (
              <Button
                size="xs"
                variant="danger"
                icon={<Link2Off className="size-3" />}
                onClick={() => link.send({ type: 'disconnect' })}
              >
                Disconnect
              </Button>
            ) : null}
          </>
        ) : null
      }
      footer={
        <span className="flex items-center gap-1.5">
          <MessageSquare className="size-3" />
          {link.canControl
            ? 'Live link — status updates arrive as they happen.'
            : 'Polled status only. Pairing controls need the live socket, so they are hidden rather than shown inert.'}
        </span>
      }
    >
      {link.loading ? (
        <Skeleton className="h-16 w-full" />
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Badge
              tone={WA_TONE[snapshot.status]}
              dot
              pulse={snapshot.status === 'connecting' || snapshot.status === 'qr'}
            >
              {snapshot.status}
            </Badge>
            {snapshot.accountJid ? <Code copyable>{snapshot.accountJid}</Code> : null}
            {snapshot.connectedAt ? (
              <span className="text-2xs text-ink-faint">
                since {dateTime(snapshot.connectedAt)}
              </span>
            ) : null}
          </div>

          {link.socketError || snapshot.reason ? (
            <Callout tone={snapshot.status === 'error' ? 'negative' : 'warn'}>
              {link.socketError ?? snapshot.reason}
            </Callout>
          ) : null}

          {snapshot.status === 'qr' && snapshot.qrDataUrl ? (
            <div className="border-signal-line bg-signal-muted flex flex-col items-center gap-2 rounded border p-3">
              <p className="text-ink text-xs">Scan with WhatsApp to link this device</p>
              <p className="text-2xs text-ink-faint">
                WhatsApp → Linked devices → Link a device
              </p>
              <img
                src={snapshot.qrDataUrl}
                alt="WhatsApp pairing QR code"
                width={220}
                height={220}
                className="rounded bg-white p-2"
              />
            </div>
          ) : null}

          {snapshot.status === 'disconnected' && !link.canControl ? (
            <p className="text-2xs text-ink-faint">
              Nothing is linked. Pairing has to be done from a session where the live
              socket is available.
            </p>
          ) : null}
        </div>
      )}
    </Panel>
  );
}
